// src/features/Planning/pages/PlanningFormPage.jsx

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  useColorModeValue,
  useBreakpointValue,
  useToast,
  Spinner,
  Center,
  Text,
  Badge,
} from "@chakra-ui/react";

import {
  isAnyAuthenticated,
  saveRedirectUrl,
  saveAdminToken,
  getAdminToken,
  getUpdatedBy,
  getCustomerToken,
} from "../../../../utils/storage";
import {
  fetchFormData,
  postFormData,
  putFormData,
  moveCardToInProgress,
} from "../../../../utils/api";

import Step1PlotShape from "../components/Step1PlotShape";
import Step2PlotDirection from "../components/Step2Direction";
import Step3Peripheries from "../components/Step3Peripheries";
import Step4PlotSize from "../components/Step4PlotSize";
import Step4PlotSizeNonRectangular from "../components/Step4PlotSizeNonRectengular";
import Step6ProjectType from "../components/Step6ProjecttypeandNumberofFloors";
import Step7MarkSetbacks from "../components/Step7MarkSetbacks";
import Step8PlotLevelInformation from "../components/Step8PlotLevelInformation";
import Step9StairType from "../components/Step9StairType";
import Step10ParkingRequirement from "../components/Step10ParkingRequirement";
import Step11GroundFloorRequirement from "../components/Step11GroundFloorRequirement";
import Step12ExistingSiteElements from "../components/Step12ExistingSiteElements";
import Step15WallDimentions from "../components/Step15WallDimentions";
import Step16WallDimentions from "../components/Step16WallDimentions";
import Step17StaircaseDimentions from "../components/Step17StaircaseDimentions";
import Step19AttachRegistryCopy from "../components/Step19AttachRegistryCopy";
import Step20ApproximateBudget from "../components/Step20ApproximateProjectBudget";

const DEFAULT_FORM_DATA = {
  shape: "rectangular",
  direction: "north",
  peripheries: { right: "", back: "", left: "" },
  roadWidthFeet: "",   // NEW — Step3: road width (feet)
  roadWidthInches: "",   // NEW — Step3: road width (inches)
  size: { widthFeet: "", widthInches: "0", depthFeet: "", depthInches: "0" },
  projectType: "",
  lifestyleType: "",   // NEW — Step6: luxury / minimal / rental-focused
  numberOfFloors: "",
  floorsData: [],
  buildingArea: null,
  setbacks: { front: "", back: "", left: "", right: "" },
  parkingRequirement: "",
  levelRelation: "same",
  stair: { type: "", riser: "", tread: "" },
  tread: "",
  riser: "",
  siteElements: { borewell: "", septicTank: "", trees: "", waterTank: "" },
  externalWallThickness: "",
  internalWallThickness: "",
  plinthLevelFeet: "", plinthLevelInches: "0",
  lintelLevelFeet: "", lintelLevelInches: "0",
  sillLevelFeet: "", sillLevelInches: "0",
  slabHeightFeet: "", slabHeightInches: "0",
  slab: { sides: "", slabExtension: "" },
  attachPlotImage: "no",
  approximateBudget: "",
  vastuPreference: "",
  // Non-rectangular plot specific
  plotDiagonal1: "",
  plotDiagonal2: "",
  cornerAngles: { BL: "", BR: "", TL: "", TR: "" },
};

const TOTAL_STEPS = 16;

export default function PlanningFormPage() {
  const { projectId: projectIdFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  // "/planning/:projectId" (path param) aur "/planning?projectId=xxx"
  // (query param) — dono URL shapes se projectId mil sake, isliye fallback.
  const projectId =
    projectIdFromPath ||
    searchParams.get("projectId") ||
    searchParams.get("pid") ||
    searchParams.get("id") ||
    "";

  const urlToken = searchParams.get("token") || "";
  const urlUpdatedBy = searchParams.get("updatedby") || "";
  const formVersion = searchParams.get("version") || "";
  const viewMode = searchParams.get("viewMode") === "true";

  if (urlToken) {
    saveAdminToken(urlToken, urlUpdatedBy);
  }

  const isAdmin = !!(urlToken || (urlUpdatedBy === "admin" && getAdminToken()));
  const token = urlToken || (isAdmin ? getAdminToken() : getCustomerToken()) || "";
  const updatedBy = isAdmin ? "admin" : (urlUpdatedBy || getUpdatedBy() || "user");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [version, setVersion] = useState(null);

  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const moveCardCalled = useRef(false);

  const containerWidth = useBreakpointValue({ base: "100%", md: "400px" });
  const pageBg = useColorModeValue("gray.100", "gray.900");
  const cardBg = useColorModeValue("gray.50", "gray.800");

  const initialize = useCallback(async () => {
    if (!token && !isAnyAuthenticated()) {
      saveRedirectUrl(window.location.pathname + window.location.search);
      toast({
        title: "Authentication Required",
        description: "Please login to continue",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
      setTimeout(() => navigate("/login"), 1500);
      return;
    }

    if (!projectId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setApiError(null);

      const apiFormData = await fetchFormData(projectId, token, updatedBy, formVersion);

      if (apiFormData._meta?.status === "submitted" && !viewMode) {
        navigate("/thank-you");
        return;
      }

      if (apiFormData._meta?.version) {
        setVersion(apiFormData._meta.version);
      }

      const { _meta, ...apiFormDataClean } = apiFormData;

      const prefilled = {
        direction: !!(apiFormDataClean.direction),
        projectType: !!(apiFormDataClean.projectType),
        numberOfFloors: !!(apiFormDataClean.numberOfFloors),
      };

      setFormData((prev) => ({
        ...prev,
        ...apiFormDataClean,
        _prefilled: prefilled,
        peripheries: { ...prev.peripheries, ...(apiFormDataClean.peripheries || {}) },
        size: { ...prev.size, ...(apiFormDataClean.size || {}) },
        siteElements: { ...prev.siteElements, ...(apiFormDataClean.siteElements || {}) },
        stair: { ...prev.stair, ...(apiFormDataClean.stair || {}) },
        stairType: apiFormDataClean.stairType || apiFormDataClean.stair?.type || prev.stairType || "",
        slab: { ...prev.slab, ...(apiFormDataClean.slab || {}) },
        slabExtension: apiFormDataClean.slabExtension ?? prev.slabExtension ?? "",
        slabSides: apiFormDataClean.slabSides ?? prev.slabSides ?? "",
        setbacks: { ...prev.setbacks, ...(apiFormDataClean.setbacks || {}) },
        cornerAngles: { ...prev.cornerAngles, ...(apiFormDataClean.cornerAngles || {}) },
      }));

      toast({
        title: "Project loaded ✓",
        description: `${projectId} — ${updatedBy === "admin" ? "Admin" : "User"} mode`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      if (!moveCardCalled.current && !viewMode) {
        moveCardCalled.current = true;
        await moveCardToInProgress(projectId, token, updatedBy, apiFormData._meta?.version || formVersion);
      }
    } catch (err) {
      console.error("API fetch failed:", err);
      setApiError(err.message);
      if (!viewMode) {
        toast({
          title: "Could not load project",
          description: `${err.message} — using blank form`,
          status: "warning",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId, token, updatedBy, navigate, toast, viewMode, formVersion]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const putToApi = async (data) => {
    if (!projectId || viewMode) return;
    setIsSaving(true);
    try {
      await putFormData(projectId, data, updatedBy, token, version);
    } catch (err) {
      console.error("Step save (PUT) failed:", err);
      toast({
        title: "Step save failed",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const postToApi = async (data) => {
    if (!projectId || viewMode) return;
    setIsSaving(true);
    try {
      await postFormData(projectId, data, updatedBy, token, version);
    } catch (err) {
      console.error("Final save (POST) failed:", err);
      toast({
        title: "Save failed",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const pickFormData = (latestData) => {
    const looksLikeEvent =
      latestData &&
      typeof latestData === "object" &&
      ("nativeEvent" in latestData ||
        "target" in latestData ||
        "_reactName" in latestData ||
        typeof latestData.preventDefault === "function");
    return latestData && typeof latestData === "object" && !looksLikeEvent
      ? latestData
      : formDataRef.current;
  };

  const nextStep = async (latestData) => {
    const dataForPut = pickFormData(latestData);
    await putToApi(dataForPut);
    setStep((p) => Math.min(p + 1, TOTAL_STEPS - 1));
  };
  const prevStep = () => setStep((p) => Math.max(p - 1, 0));
  const isLastStep = step === TOTAL_STEPS - 1;

  const handleSubmit = async (latestData) => {
    if (viewMode) return;
    const dataForPost = pickFormData(latestData);
    await postToApi(dataForPost);
    navigate("/thank-you");
  };

  const renderStep = () => {
    const props = {
      formData, setFormData,
      onBack: prevStep, onNext: nextStep, onSubmit: handleSubmit,
      isLastStep, projectId, updatedBy, token, viewMode,
    };
    switch (step) {
      case 0: return <Step1PlotShape {...props} />;
      case 1: return <Step2PlotDirection {...props} />;
      case 2: return <Step3Peripheries {...props} />;
      case 3: return formData.shape === "rectangular"
        ? <Step4PlotSize {...props} />
        : <Step4PlotSizeNonRectangular {...props} />;
      case 4: return <Step6ProjectType {...props} />;
      case 5: return <Step7MarkSetbacks {...props} />;
      case 6: return <Step8PlotLevelInformation {...props} />;
      case 7: return <Step9StairType {...props} />;
      case 8: return <Step10ParkingRequirement {...props} />;
      case 9: return <Step11GroundFloorRequirement {...props} />;
      case 10: return <Step12ExistingSiteElements {...props} />;
      case 11: return <Step15WallDimentions {...props} />;
      case 12: return <Step16WallDimentions {...props} />;
      case 13: return <Step17StaircaseDimentions {...props} />;
      case 14: return <Step19AttachRegistryCopy {...props} />;
      case 15: return <Step20ApproximateBudget {...props} />;
      default: return null;
    }
  };

  if (isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
        <Center flexDirection="column" gap={4}>
          <Spinner size="xl" color="cyan.500" thickness="4px" />
          <Text color="gray.500" fontSize="sm">Loading {projectId}...</Text>
        </Center>
      </Flex>
    );
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
      <Box
        w={containerWidth}
        bg={cardBg}
        px={6} py={6}
        borderRadius="lg"
        boxShadow="lg"
        display="flex"
        flexDirection="column"
      >
        <Flex justify="space-between" align="center" mb={2}>
          <Flex align="center" gap={2}>
            <Text fontSize="11px" color="gray.400" fontWeight="500">
              Step {step + 1} / {TOTAL_STEPS}
            </Text>
            {projectId && (
              <Badge fontSize="10px" colorScheme="cyan" variant="subtle">
                {projectId}
              </Badge>
            )}
            {updatedBy === "admin" && (
              <Badge fontSize="10px" colorScheme="purple" variant="subtle">
                Admin
              </Badge>
            )}
            {viewMode && (
              <Badge fontSize="10px" colorScheme="orange" variant="solid">
                View Mode
              </Badge>
            )}
          </Flex>
          <Flex align="center" gap={1}>
            {isSaving && (
              <Text fontSize="10px" color="cyan.500" fontWeight="600">
                Saving...
              </Text>
            )}
            {apiError && (
              <Badge fontSize="10px" colorScheme="orange" variant="subtle">
                ⚠ Offline
              </Badge>
            )}
          </Flex>
        </Flex>

        <Box bg="gray.200" borderRadius="full" h="3px" mb={4}>
          <Box
            bg="cyan.500"
            borderRadius="full"
            h="3px"
            w={`${(step / TOTAL_STEPS) * 100}%`}
            transition="width 0.3s ease"
          />
        </Box>

        <Box flex="1">{renderStep()}</Box>
      </Box>
    </Flex>
  );
}